import { useState, useEffect } from "react";

const WHATSAPP_URL = "https://wa.me/5547999300330";

const navLinks = [
  { label: "Escritório", href: "#escritorio" },
  { label: "Áreas de Atuação", href: "#areas" },
  { label: "Como Atuamos", href: "#como-atuamos" },
  { label: "Contato", href: "#contato" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const handleNavClick = (href: string) => {
    setMobileOpen(false);
    setTimeout(() => {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "bg-[#6E0C10] shadow-lg py-3" : "bg-[#8B0F14] py-5"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-10 flex items-center justify-between">
          {/* Logo */}
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            className="cursor-pointer flex-shrink-0 hidden md:block"
          >
            <img
              src="https://static.readdy.ai/image/2751d92eb47f633f00285d5acf7c03f9/ecce661685ad1d395f8a319d08998900.png"
              alt="Lupepsa & Marconatto Advocacia"
              style={{ width: "92px" }}
              className="w-[92px] h-auto object-contain"
            />
          </a>

          {/* Mobile: spacer left + logo center + hamburger right */}
          <div className="md:hidden w-10 h-10" />

          {/* Mobile center logo */}
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            className="md:hidden cursor-pointer absolute left-1/2 -translate-x-1/2"
          >
            <img
              src="https://static.readdy.ai/image/2751d92eb47f633f00285d5acf7c03f9/ecce661685ad1d395f8a319d08998900.png"
              alt="Lupepsa & Marconatto Advocacia"
              style={{ width: "72px" }}
              className="w-[72px] h-auto object-contain"
            />
          </a>

          {/* Mobile hamburger — right side */}
          <button
            className="md:hidden w-10 h-10 flex items-center justify-center text-[#E3B24A] cursor-pointer z-10"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Abrir menu"
          >
            <i className="ri-menu-3-line text-2xl" />
          </button>

          {/* Desktop Nav - right side */}
          <nav className="hidden md:flex items-center gap-6 lg:gap-8 ml-auto">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNavClick(link.href)}
                className="text-[#F2D27A] font-body text-sm font-medium tracking-wide hover:text-[#E3B24A] transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                {link.label}
              </button>
            ))}
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-[#E3B24A] hover:bg-[#F2D27A] text-[#6E0C10] font-body font-semibold text-sm px-5 py-2.5 rounded-full transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer ml-2"
            >
              <i className="ri-whatsapp-line text-base" />
              Falar pelo WhatsApp
            </a>
          </nav>
        </div>
      </header>

      {/* Mobile Full-Screen Overlay Menu */}
      <div
        className={`fixed inset-0 z-[60] md:hidden transition-all duration-400 ${
          mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        style={{ background: "#6E0C10" }}
      >
        {/* Close button */}
        <button
          className="absolute top-5 right-4 w-11 h-11 flex items-center justify-center text-[#E3B24A] cursor-pointer"
          onClick={() => setMobileOpen(false)}
          aria-label="Fechar menu"
        >
          <i className="ri-close-line text-3xl" />
        </button>

        {/* Logo centered */}
        <div className="flex justify-center pt-12 mb-12">
          <img
            src="https://static.readdy.ai/image/2751d92eb47f633f00285d5acf7c03f9/ecce661685ad1d395f8a319d08998900.png"
            alt="Lupepsa & Marconatto Advocacia"
            style={{ width: "100px" }}
            className="w-[100px] h-auto object-contain"
          />
        </div>

        {/* Divider */}
        <div className="flex items-center gap-4 px-8 mb-10">
          <div className="h-px flex-1 bg-[#E3B24A]/20" />
          <div className="w-1.5 h-1.5 rounded-full bg-[#E3B24A]/40" />
          <div className="h-px flex-1 bg-[#E3B24A]/20" />
        </div>

        {/* Nav links */}
        <nav className="flex flex-col items-center gap-6 px-8">
          {navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => handleNavClick(link.href)}
              className="text-[#F2D27A] font-body text-lg font-medium tracking-widest uppercase text-center hover:text-[#E3B24A] transition-colors cursor-pointer"
            >
              {link.label}
            </button>
          ))}
        </nav>

        {/* WhatsApp CTA */}
        <div className="absolute bottom-14 left-0 right-0 flex justify-center px-8">
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-3 bg-[#E3B24A] text-[#6E0C10] font-body font-semibold text-base px-8 py-4 rounded-full whitespace-nowrap cursor-pointer w-full max-w-xs"
          >
            <i className="ri-whatsapp-line text-xl" />
            Falar pelo WhatsApp
          </a>
        </div>
      </div>
    </>
  );
}
